import {Alert, Button, Card, Separator} from 'platform/components';
import {
  Box,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Show,
  Spinner,
  Text,
  VStack,
} from 'platform/foundation';

import {useState, useSyncExternalStore} from 'react';

import {isNil} from 'ramda';

import {
  type InterestRecommendation,
  type InterestDecisionReasonCodeValue,
} from '@dms/api/interest-recommendations';
import i18n from '@dms/i18n';

import {type MyDayData} from '../../types/MyDayData';
import {RecommendationRowView} from '../RecommendationRowView/RecommendationRowView';
import {MyDayHero} from './components/MyDayHero';
import {MyDayHistory} from './components/MyDayHistory';
import {MyDayMetrics} from './components/MyDayMetrics';
import {MyDayPortfolio} from './components/MyDayPortfolio';
import {MyDayRecommendationDetail} from './components/MyDayRecommendationDetail';

interface MyDayViewProps {
  /** Change with tenant, branch, user, or permission scope; pass only that scope's data. */
  contextKey: string;
  data?: MyDayData;
  isPreview?: boolean;
  status: 'loading' | 'ready' | 'refreshing' | 'error';
  onRefresh: () => void;
  onOpenInterest?: (interestId: string) => void;
  /** Opens an available communication workflow; opening it never completes a recommendation. */
  onAction?: (recommendation: InterestRecommendation) => void;
  onReject?: (
    recommendation: InterestRecommendation,
    reason: InterestDecisionReasonCodeValue
  ) => void;
  onUndo?: (decisionId: string) => void;
  onLoadMoreRecommendations?: () => void;
  onLoadMoreHistory?: () => void;
}

// The prototype places the detail below the list at 1200 CSS pixels.
const WIDE_LAYOUT_QUERY = '(min-width: 75rem)';
const isWideLayout = () => window.matchMedia?.(WIDE_LAYOUT_QUERY).matches ?? true;
const subscribeToLayout = (onChange: () => void) => {
  const query = window.matchMedia?.(WIDE_LAYOUT_QUERY);
  query?.addEventListener('change', onChange);
  return () => query?.removeEventListener('change', onChange);
};

export function MyDayView(props: MyDayViewProps) {
  return <MyDayViewContent key={props.contextKey} {...props} />;
}

function MyDayViewContent(props: MyDayViewProps) {
  const [selectedInterestId, setSelectedInterestId] = useState<string>();
  const isWide = useSyncExternalStore(subscribeToLayout, isWideLayout);
  const data = props.status === 'loading' ? undefined : props.data;
  const recommendations = data?.recommendations.filter((entry) => entry.signals.length > 0) ?? [];
  const selectedRecommendation = isNil(selectedInterestId)
    ? recommendations[0]
    : recommendations.find((entry) => entry.interestId === selectedInterestId);
  const coverage = data?.coverage;
  const isIncomplete =
    !isNil(coverage) &&
    (coverage.evaluated < coverage.eligible ||
      coverage.skipped > 0 ||
      coverage.failed > 0 ||
      coverage.omissions.length > 0);
  const isPartial =
    isIncomplete ||
    Boolean(data?.hasMoreRecommendations) ||
    (recommendations.length === 0 && (data?.totals?.recommended ?? 0) > 0);
  const isLoading = props.status === 'loading' || props.status === 'refreshing';
  const onAction = props.status === 'ready' ? props.onAction : undefined;
  const coverageSummary = isNil(coverage)
    ? undefined
    : i18n.t('page.interestMyDay.coverage.summary', {
        evaluated: coverage.evaluated,
        eligible: coverage.eligible,
        skipped: coverage.skipped,
        failed: coverage.failed,
      });

  return (
    <VStack spacing={6}>
      <MyDayHero
        evaluatedAt={data?.evaluatedAt}
        isLoading={isLoading}
        isPreview={props.isPreview}
        onRefresh={props.onRefresh}
      />

      <Show when={props.status === 'error'}>
        <Alert variant="error" message={i18n.t('aiAssistant.interest.focus.failureBody')} />
      </Show>

      <Show when={data?.totals}>{(totals) => <MyDayMetrics totals={totals} />}</Show>
      <Show when={isLoading}>
        <HStack spacing={2} align="center">
          <Spinner size="small" />
          <Text role="status">{i18n.t('general.labels.loading')}</Text>
        </HStack>
      </Show>

      <Show when={isIncomplete}>
        <Alert variant="warning" message={i18n.t('page.interestMyDay.coverage.incomplete')} />
      </Show>

      <Show when={!isNil(data) && recommendations.length === 0 && props.status === 'ready'}>
        <Card>
          <VStack spacing={3}>
            <Show when={coverageSummary}>
              {(summary) => (
                <Text size="xSmall" color="secondary">
                  {summary}
                </Text>
              )}
            </Show>
            <Text role="status">
              {isPartial
                ? i18n.t('page.interestMyDay.empty.partial')
                : i18n.t('aiAssistant.interest.focus.emptyBody')}
            </Text>
          </VStack>
        </Card>
      </Show>

      <Show when={recommendations.length > 0}>
        <Grid columns={isWide ? 3 : 1} spacing={4} align="flex-start">
          <GridItem span={isWide ? 2 : 1}>
            <Box
              backgroundColor="general.white"
              borderRadius="medium"
              boxShadow="elevation_1"
              overflow="hidden"
            >
              <Box padding={4}>
                <VStack spacing={2}>
                  <HStack spacing={2} align="center">
                    <Icon
                      value="action/assignment_turned_in"
                      size={4}
                      color="palettes.purple.60.100"
                    />
                    <Heading size={4} headingLevel={2}>
                      {i18n.t('page.interestMyDay.recommendations.title')}
                    </Heading>
                  </HStack>
                  <Show when={coverageSummary}>
                    {(summary) => (
                      <Text size="xSmall" color="secondary">
                        {summary}
                      </Text>
                    )}
                  </Show>
                </VStack>
              </Box>
              <Separator spacing={0} />
              <Box role="list">
                <VStack>
                  {recommendations.map((recommendation, index) => (
                    <Box role="listitem" key={recommendation.interestId}>
                      <RecommendationRowView
                        recommendation={recommendation}
                        position={index + 1}
                        isSelected={
                          recommendation.interestId === selectedRecommendation?.interestId
                        }
                        onSelect={setSelectedInterestId}
                        onAction={onAction}
                        vehicleLabel={
                          data?.contextByInterestId?.[recommendation.interestId]?.vehicleLabel
                        }
                        onReject={
                          props.status === 'ready' && props.onReject
                            ? (reason) => props.onReject?.(recommendation, reason)
                            : undefined
                        }
                      />
                      <Show when={index < recommendations.length - 1}>
                        <Separator spacing={0} />
                      </Show>
                    </Box>
                  ))}
                </VStack>
              </Box>
            </Box>
          </GridItem>
          <GridItem>
            <Box position={isWide ? 'sticky' : 'static'} top={4}>
              <Show when={selectedRecommendation}>
                {(recommendation) => (
                  <MyDayRecommendationDetail
                    recommendation={recommendation}
                    onOpenInterest={props.onOpenInterest}
                    context={data?.contextByInterestId?.[recommendation.interestId]}
                    onAction={onAction}
                  />
                )}
              </Show>
              <Show when={isNil(selectedRecommendation)}>
                <Card>
                  <Text role="status">{i18n.t('page.interestMyDay.details.select')}</Text>
                </Card>
              </Show>
            </Box>
          </GridItem>
        </Grid>
      </Show>
      <Show when={data?.hasMoreRecommendations}>
        <VStack spacing={3}>
          <Text size="small" color="secondary">
            {i18n.t('page.interestMyDay.coverage.more')}
          </Text>
          <Button
            variant="secondary"
            title={i18n.t('entity.automationRule.list.loadMore')}
            isDisabled={props.status !== 'ready' || isNil(props.onLoadMoreRecommendations)}
            onClick={props.onLoadMoreRecommendations}
          />
        </VStack>
      </Show>
      <Show when={data?.history}>
        {(history) => (
          <MyDayHistory
            history={history}
            isDisabled={props.status !== 'ready'}
            onLoadMore={props.onLoadMoreHistory}
            onUndo={props.onUndo}
          />
        )}
      </Show>
      <Show when={data && (data.saleFunnel || data.totals) ? data : undefined}>
        {(result) => (
          <MyDayPortfolio data={result} isWide={isWide} onSelect={setSelectedInterestId} />
        )}
      </Show>
    </VStack>
  );
}
